import { Injectable } from '@angular/core';
import { Client, Profile } from 'core/index';
import { DashboardWidgets } from '@widgets/widget';
import {
  GroupListComponent,
  CreditsListComponent,
  MyPlanWidget,
  CourseStudentWidget,
  TrainingHourWidget,
  StudentCommentWidget,
  ScheduledClassWidget,
  SubscriptionActivationWidget,
  MetricWidget,
  NpsWidget,
  AutoevaluationWidget,
  AutoevaluationCoursesWidget,
  ApplicabilityComponentWidget
} from '../dashboard-widgets';
import { Dashboard } from '@widgets/models/Dashboard';

@Injectable()
export class DashboardWidgetsService {
  constructor() {}
  public getWidgets(user: Profile, dashboardData: Dashboard, client: Client): DashboardWidgets {
    if (dashboardData?.clientIds?.length) {
      return this.getClientDashboardWidgets(user, dashboardData, client);
    }

    const role = user.roleOptions;
    switch (role.name) {
      case 'superAdmin':
      case 'supportSpecialist':
      case 'csm':
      case 'csAdmin':
        return {
          dashboardType: 'old',
          widgets: [GroupListComponent(user, role, 6, null), CreditsListComponent(user, role, 7, null)]
        };
      case 'clientAdmin':
      case 'hr':
      case 'dachAdmin':
        return this.getClientDashboardWidgets(user, dashboardData);
    }
  }

  public getClientDashboardWidgets(user: Profile, dashboardData: Dashboard, _client?: Client): DashboardWidgets {
    const { clientIds, managedAccounts } = dashboardData;
    const client = managedAccounts.length === 1 ? managedAccounts[0] : null;
    const role = user.roleOptions;
    // hidden for scorm clients, when are many clients it's shown if some client is not scorm
    const shouldShowScheduledClassWidget = managedAccounts.some((client) => !client.scorm.enabled);
    // applicability widget available when has only one client with notifications.n8, or if has surveys for many clients
    const isAvailableN8Client =
      !!(client && client.notificationsCfg?.n8?.enabled) || !dashboardData.metricApplicability.empty;

    const activation = !dashboardData?.activation?.empty;

    const widgets = [];

    if (dashboardData.seats) {
      widgets.push(MyPlanWidget(user, role, 0, dashboardData.seats, 'seats', managedAccounts));
    }
    if (dashboardData.credits) {
      widgets.push(MyPlanWidget(user, role, 0, dashboardData.credits, 'credits', managedAccounts));
    }

    if (activation) {
      widgets.push(SubscriptionActivationWidget(user, role, 0, dashboardData.activation, 'subscriptionActivation'));
      widgets.push(MetricWidget(user, role, 1, dashboardData.metricData, clientIds, managedAccounts));
      widgets.push(TrainingHourWidget(user, role, 2, dashboardData.trainingHours, clientIds, managedAccounts));
    }

    if (!activation) {
      widgets.push(TrainingHourWidget(user, role, 0, dashboardData.trainingHours, clientIds, managedAccounts));
      widgets.push(MetricWidget(user, role, 1, dashboardData.metricData, clientIds, managedAccounts));
      widgets.push(AutoevaluationWidget(user, role, 2, dashboardData.autoevaluations));
    }

    if (shouldShowScheduledClassWidget) {
      widgets.push(ScheduledClassWidget(user, role, 3, dashboardData.scheduledClass, client));
    }

    widgets.push(
      AutoevaluationCoursesWidget(user, role, 4, dashboardData.coursesAutoevaluations, clientIds, managedAccounts),
      NpsWidget(user, role, 5, dashboardData.metricNps, clientIds, managedAccounts),
      CourseStudentWidget(user, role, 6, dashboardData.studentCourses)
    );

    if (isAvailableN8Client) {
      widgets.push(ApplicabilityComponentWidget(user, role, 7, dashboardData.metricApplicability, managedAccounts));
    }

    if (activation) {
      widgets.push(AutoevaluationWidget(user, role, 8, dashboardData.autoevaluations));
    }

    widgets.push(StudentCommentWidget(user, role, 9, dashboardData.studentComments));

    return {
      dashboardType: 'new',
      widgets
    };
  }
}
